import type { Id } from "@repo/convex/dataModel";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatDuration } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import type {
  AssistantActivityTurnItem,
  AssistantActivityTurnTimelineItem,
  AssistantTranscriptNarration,
} from "../types";
import { useActivityBlockExpansion } from "./assistant-activity-turn/activity-block-expansion";
import { getAssistantActivityIconName } from "./assistant-activity-turn/activity-icons";
import {
  getActivityTurnItemFlags,
  getRenderableBlocks,
} from "./assistant-activity-turn/activity-model";
import { NarrationLine, ThinkingFooter } from "./assistant-activity-turn/activity-rows";
import { ActivityTurnBody } from "./assistant-activity-turn/activity-turn-body";
import { ActivityTurnHeader } from "./assistant-activity-turn/activity-turn-header";
import { AnimatedCollapse } from "./assistant-activity-turn/animated-collapse";
import { useLiveDuration } from "./assistant-activity-turn/live-duration";

export { getAssistantActivityIconName };

const STALLED_NARRATION_DELAY_MS = 2500;

function isSyntheticFallbackPreamble(item: AssistantActivityTurnItem) {
  return item.kind === "preamble" && item.source === "system" && item.status === "in_progress";
}

interface AssistantActivityTurnProps {
  item: AssistantActivityTurnTimelineItem;
  projectId: Id<"projects">;
  narration?: AssistantTranscriptNarration | null;
  hasActiveAssistantMessage?: boolean;
  compact?: boolean;
}

/** The turn header counts elapsed work time up as it runs: "3m 05s", not "185.0 s". */
const ELAPSED = { style: "clock" } as const;

export function AssistantActivityTurn({
  item,
  projectId,
  narration,
  hasActiveAssistantMessage = false,
  compact = false,
}: AssistantActivityTurnProps) {
  const isLive = item.status === "in_progress";
  const [expanded, setExpanded] = useState(isLive);
  const [isNarrationStalled, setIsNarrationStalled] = useState(false);

  const liveDuration = useLiveDuration(item.startedAt, isLive, item.pauseIntervals);
  const durationLabel = useMemo(() => {
    if (isLive && typeof liveDuration.durationMs === "number") {
      return `${liveDuration.isPaused ? "Worked" : "Working"} for ${formatDuration(
        liveDuration.durationMs,
        ELAPSED
      )}`;
    }
    const completedDurationMs = item.activeDurationMs ?? item.durationMs;
    if (typeof completedDurationMs === "number") {
      return `Worked for ${formatDuration(completedDurationMs, ELAPSED)}`;
    }
    return null;
  }, [
    isLive,
    item.activeDurationMs,
    item.durationMs,
    liveDuration.durationMs,
    liveDuration.isPaused,
  ]);

  const headerLabel =
    !isLive && durationLabel ? `${durationLabel} - ${item.summaryText}` : item.summaryText;
  const headerParts =
    !isLive && durationLabel && item.summaryTextParts
      ? [{ kind: "text" as const, text: `${durationLabel} - ` }, ...item.summaryTextParts]
      : item.summaryTextParts;
  const headerHasLinks = Boolean(headerParts?.length);
  const narrationText = narration?.text ?? "";
  const hasNarrationText = Boolean(narrationText.trim());
  const visibleItem = useMemo(() => {
    if (!hasNarrationText || !item.items.some(isSyntheticFallbackPreamble)) {
      return item;
    }
    return {
      ...item,
      items: item.items.filter((turnItem) => !isSyntheticFallbackPreamble(turnItem)),
    };
  }, [hasNarrationText, item]);
  const visibleRenderableBlocks = useMemo(
    () => getRenderableBlocks({ blocks: visibleItem.blocks, items: visibleItem.items }),
    [visibleItem.blocks, visibleItem.items]
  );
  const { expandedBlocks, toggleBlock } = useActivityBlockExpansion(visibleRenderableBlocks);
  const itemFlags = useMemo(() => getActivityTurnItemFlags(visibleItem.items), [visibleItem.items]);
  const shouldShowThinking =
    isLive &&
    !hasNarrationText &&
    !hasActiveAssistantMessage &&
    !itemFlags.hasActiveWorkItem &&
    !itemFlags.hasWaitingApprovalItem &&
    !itemFlags.hasWaitingUserInputItem &&
    !itemFlags.hasWaitingItem;
  const canExpand =
    !isLive &&
    (item.items.length > 1 || (item.blocks?.length ?? 0) > 0 || itemFlags.hasExpandableItem);

  const toggleExpanded = useCallback(() => {
    setExpanded((current) => !current);
  }, []);

  useEffect(() => {
    if (isLive) {
      setExpanded(true);
      return;
    }
    setExpanded(false);
  }, [isLive]);

  useEffect(() => {
    if (!isLive || !narrationText.trim()) {
      setIsNarrationStalled(false);
      return;
    }

    setIsNarrationStalled(false);
    const timeoutId = window.setTimeout(() => {
      setIsNarrationStalled(true);
    }, STALLED_NARRATION_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isLive, narrationText]);

  return (
    <section
      className={cn("w-full", compact ? "pl-0" : "pl-9")}
      data-testid="assistant-activity-turn"
      data-live={isLive ? "true" : "false"}
    >
      {!isLive ? (
        <ActivityTurnHeader
          canExpand={canExpand}
          expanded={expanded}
          headerHasLinks={headerHasLinks}
          headerLabel={headerLabel}
          headerParts={headerParts}
          onToggle={toggleExpanded}
        />
      ) : null}

      <AnimatedCollapse open={expanded} enterDurationMs={isLive ? 180 : 210} exitDurationMs={170}>
        <div className={cn("space-y-4", !isLive && "mt-2")}>
          {durationLabel && isLive ? (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">{durationLabel}</p>
              <div className="border-b border-border/60" />
            </div>
          ) : null}

          <ActivityTurnBody
            item={visibleItem}
            projectId={projectId}
            compact={compact}
            expandedBlocks={expandedBlocks}
            renderableBlocks={visibleRenderableBlocks}
            onToggleBlock={toggleBlock}
          />

          {hasNarrationText ? (
            <>
              <NarrationLine text={narrationText} compact={compact} />
              {isNarrationStalled ? <ThinkingFooter /> : null}
            </>
          ) : shouldShowThinking ? (
            <ThinkingFooter />
          ) : null}
        </div>
      </AnimatedCollapse>
    </section>
  );
}
